import React, { useEffect, useState } from 'react';
import DynamicTable from '../../components/DynamicTables';
import { useSetting } from '../../contexts/SettingContext';
import { FormField } from '../../interfaces/User.interface';
import { useFetch } from '../../utils/FetchTemplate';
import { IService } from '../../interfaces/Setting.interface';
import PopupConfirm from '../../components/PopupConfirm';
import { useMembers } from '../../contexts/MemberContext';

import {Dialog, DialogTitle, DialogContent, Box, Typography} from '@mui/material';

import ServiceDetailDialog from './ServiceDetailDialog';
import { Role, ServiceType } from '../../interfaces/constants';
import ViewServiceDialog from './ViewServiceDialog';



const ServiceTable: React.FC = () => {
    const { settings, fetchSettings } = useSetting();
    const { postData, getData } = useFetch()
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [openPopup, setOpenPopup] = useState<boolean>(false);
    const [selectedType, setSelectedType] = useState<string>("");

    const { members } = useMembers();

    const [viewData, setViewData] = useState<any>(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);

    const [selectedService, setSelectedService] = useState<any>(null);
    const [openServiceDialog, setOpenServiceDialog] = useState(false);
    
    // Get current user role
    const currentUserRole = settings?.profile?.role;
    const isAdmin = currentUserRole === Role.Admin;
    
    useEffect(() => {
        fetchSettings();
    }, []);

    const baseFields: FormField[] = [
        { name: 'name', question: 'Service Name', validation: 'text', required: true, questionType: 'text', inputProps: { placeholder: 'Service Name'}, props: { variant: 'outlined' }},
        { name: 'type', question: 'Type', validation: 'text', required: true, questionType: 'autocomplete', data: Object.values(ServiceType), props: { variant: 'outlined' }},
        { name:'rate', question: 'Tax Rate (%)', validation: 'number', required: true, questionType: 'number' },
        { name: 'description', question: 'Description', validation: 'text', required: false, questionType: 'text', props: { variant: 'outlined' }},
        // Conditionally include teacher field only if user is not Admin or Student
        ...(isAdmin ? [{ 
            name: 'teacherId', 
            question: 'Teacher', 
            validation: 'object', 
            required: true, 
            questionType: 'autocomplete',
            data: members.filter(m => m.role === Role.Teacher),
            props: {
              getOptionLabel: (option: any) => `${option.firstName} ${option.lastName}`,
              isOptionEqualToValue: (option: any, value: any) => option.memberId === value.memberId,
              placeholder: 'Search member',
              variant: 'outlined',
            },
            displayFn: (value: any) => {
                if (!value) return '';
                const teacher = members.find(m => m.memberId === (typeof value === 'string' ? value : value.memberId));
                return teacher ? `${teacher.firstName} ${teacher.lastName}` : value;
            },
          }] : []),
        {name: 'salary', question: 'Salary (CAD)', validation: 'number', required: true, questionType: 'number'},
    ];

    let dynamicFields: FormField[] = [];
    if (selectedType === ServiceType.Group) {
        dynamicFields = [
            {name: 'groupSize', question: 'Group Size', validation: 'number', required: true, questionType: 'number'},
            {name: 'price', question: 'Price (CAD)', validation: 'number', required: true, questionType: 'number'},
        ]
    }
    else if (selectedType === ServiceType.VIP) {
        const oneVOnePrices = Array.from({ length: 21 }, (_, i) => 100 + i * 10);
        dynamicFields = [
            {name: 'price', question: 'Price (CAD)', validation: 'number', required: true, questionType: 'autocomplete', data: oneVOnePrices},
        ];
    } 

    const formFields = [...baseFields, ...dynamicFields];
    const hiddenKeys = ['serviceId', 'createdAt', '_id', 'teacherId', 'studentMemberIds', 'paymentIds'];

    // Create read-only handlers for Admin and Student roles
    const readOnlyHandlers = {
        fetchSettings: () => {
            return fetchSettings().finally(() => {
            });
        },
        handleCreate: () => Promise.resolve(), // No-op for read-only
        handleEdit: () => Promise.resolve(), // No-op for read-only 
        handleDelete: () => Promise.resolve(), // No-op for read-only
        handleView: async (data: any) => {
            setViewData(data);
            setOpenViewDialog(true);
        }
    };

    const editableHandlers = {
        fetchSettings: () => {
            return fetchSettings().finally(() => {
            });
        },
        handleCreate: (data: any) => {
            const teacherId = data.teacherId?.memberId;

            if (settings.services.some((service: IService) => service.name === data.name && service.type === data.type)){
                setErrorMessage('Duplicate Service Name');
                setOpenPopup(true);
                return Promise.resolve();
            }

            //set group size = 1 when type is 1v1
            if (data.type == ServiceType.VIP) {
                data.groupSize = 1;
            }

            const fetchData = {
                service: {
                    ...data,
                    teacherId, //service 关联 teacherid
                },
                userId: settings.userId
            };

            return postData(`/api/setting/service/create`, fetchData).catch().finally(() => {
            });
        },
        handleEdit: (data: any) => {
            const teacherId = typeof data.teacherId === 'object' ? data.teacherId.memberId : data.teacherId;

            const fetchData = {
                serviceId: data.serviceId,
                userId: settings.userId,
                updateData: {
                    ...data,
                    teacherId,
                },
            }
            return postData(`/api/setting/service/update`, fetchData).catch().finally(() => {
            });
        },
        handleDelete: (data: any) => {
            return postData('/api/setting/service/delete', { serviceId: data.serviceId, userId: settings.userId }).catch().finally(() => {
                editableHandlers.fetchSettings();
            });
        },

        handleView: async (data: any) => {
            setViewData(data);
            setOpenViewDialog(true);
        }
    };

    // Use appropriate handlers based on user role
    const handlers = isAdmin ? editableHandlers : readOnlyHandlers;
    
    //用memberId搜索对应老师对象
    let servicesWithFullMember: any[] = [];

    const renderTable = settings.services !== undefined;
    if (renderTable) {
        servicesWithFullMember = settings.services.map(service => {
            const teacher = members.find(m => m.memberId === service.teacherId);
            return {
              ...service,
              teacherId: teacher ?? "undefined",
            };
        });
      }
    

    return (   
        <>
            {renderTable && <DynamicTable tableName="Service" 
                                            formFields={formFields} 
                                            data={servicesWithFullMember} 
                                            handlers={handlers} 
                                            onFormChange={(field, value) => {
                                                if (field === 'type') setSelectedType(value); 
                                            }}
                                            onRowClick={(row) => {
                                                setSelectedService(row);
                                                setOpenServiceDialog(true);
                                            }}
                                            // Control editing permissions based on user role
                                            allowEdit={isAdmin}
                                            allowDelete={isAdmin}
                                            allowView={true}/>}
            {openPopup && <PopupConfirm message={errorMessage} onConfirm={() => setOpenPopup(false)} />}

            <ServiceDetailDialog open={openServiceDialog} service={selectedService} onClose={() => setOpenServiceDialog(false)} />

            <ViewServiceDialog
                open={openViewDialog}
                onClose={() => setOpenViewDialog(false)}
                service={viewData}
            />
        </>
    );
};

export default ServiceTable;
